'use client';

import { useEffect, useState } from 'react';
import {
  hydratedArtifactMarkdown,
  hydratedEvidenceMarkdown,
} from '@/components/mobile-citations/citation-evidence';
import {
  hydrateEvidence,
  type MobileUiConfigArtifact,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import type { MappedMessage } from '@/shared/mobile-service/lib/message-mapping';
import { CitedRowMarkdownPane } from '../table/CitedRowMarkdownPane';

/**
 * Full artifact markdown for one clicked message.
 *
 * The bubble row only selected the fields the thread needs. Hydrating the
 * artifact the same way a table row does returns every populated field.
 */
export function MessageMarkdownPane({
  message,
  artifact,
  mobileAcquisitionId,
  title,
  onClose,
}: {
  message: MappedMessage;
  artifact: MobileUiConfigArtifact;
  mobileAcquisitionId: number | null;
  title: string;
  onClose: () => void;
}) {
  const { cellId, accessToken, enabled: sessionEnabled } = useCellServiceSession();
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const numericId = Number(message.id);
    if (!Number.isFinite(numericId) || mobileAcquisitionId == null) {
      setMarkdown(hydratedArtifactMarkdown(message.row, title));
      setIsLoading(false);
      setError(null);
      return;
    }
    if (!sessionEnabled || !cellId || !accessToken) return;

    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    void hydrateEvidence({
      cellId,
      accessToken,
      locator: {
        kind: 'artifact',
        mobileAcquisitionId,
        mobileArtifactId: numericId,
        graphqlType: artifact.graphql_type,
        endpoint: artifact.endpoint,
        queryName: artifact.front_end?.graphql_root_query,
        requestedView: 'markdown',
      },
      limit: 1,
      signal: controller.signal,
    })
      .then((response) => {
        if (controller.signal.aborted) return;
        const item = response.items[0];
        setMarkdown(
          (item ? hydratedEvidenceMarkdown(item) : null) ??
            hydratedArtifactMarkdown(item ?? message.row, title),
        );
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(caught instanceof Error ? caught : new Error(String(caught)));
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [
    accessToken,
    artifact.endpoint,
    artifact.front_end?.graphql_root_query,
    artifact.graphql_type,
    cellId,
    message.id,
    message.row,
    mobileAcquisitionId,
    reloadKey,
    sessionEnabled,
    title,
  ]);

  return (
    <CitedRowMarkdownPane
      markdown={markdown}
      isLoading={isLoading}
      error={error}
      title={title}
      onRetry={() => setReloadKey((key) => key + 1)}
      onClose={onClose}
    />
  );
}
